import { Button, Notice } from "../components/ui";
import { inPlanningPeriod, planningYearLabel, planningYearPeriod } from "./planning-period";

const collator = new Intl.Collator("de-CH", { numeric: true, sensitivity: "base" });
const day = 24 * 60 * 60 * 1000;
const isoTime = (value) => {
  const time = Date.parse(`${value}T00:00:00Z`);
  return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
};
const dateLabel = (value) => value ? new Date(`${value}T00:00:00Z`).toLocaleDateString("de-CH") : "–";
const labelMap = (items) => Object.fromEntries(items.map((item) => [item.id, item.name || item.code || item.id]));

export function groupProjectPlanning(entries, employees = [], phases = [], dispositions = []) {
  const employeeNames = labelMap(employees);
  const phaseNames = labelMap(phases);
  const dispositionNames = labelMap(dispositions);
  const phaseOrder = Object.fromEntries(phases.map((phase, index) => [phase.id, index]));
  const normalized = entries.map((entry) => ({
    ...entry,
    employeeName: employeeNames[entry.mitarbeiterId] || entry.mitarbeiterId || "Unbekannt",
    phaseName: phaseNames[entry.phaseId] || entry.phaseId || "Ohne Phase",
    dispositionName: dispositionNames[entry.dispositionsstatusId] || entry.dispositionsstatusId || "–",
  })).sort((a, b) =>
    (phaseOrder[a.phaseId] ?? Number.MAX_SAFE_INTEGER) - (phaseOrder[b.phaseId] ?? Number.MAX_SAFE_INTEGER) ||
    collator.compare(a.phaseName, b.phaseName) || isoTime(a.datum) - isoTime(b.datum) ||
    collator.compare(a.employeeName, b.employeeName) || Number(a.stunden) - Number(b.stunden) ||
    collator.compare(a.dispositionName, b.dispositionName) || collator.compare(a.inhouseExtern || "", b.inhouseExtern || "") ||
    collator.compare(a.halbtag || "", b.halbtag || "") || collator.compare(a.kommentar || "", b.kommentar || "") || collator.compare(a.id || "", b.id || "")
  );
  const phaseGroups = new Map();
  const equivalent = ["mitarbeiterId", "stunden", "dispositionsstatusId", "inhouseExtern", "halbtag", "kommentar"];
  normalized.forEach((entry) => {
    const phaseKey = entry.phaseId || "no-phase";
    if (!phaseGroups.has(phaseKey)) phaseGroups.set(phaseKey, { id: phaseKey, name: entry.phaseName, order: phaseOrder[entry.phaseId] ?? Number.MAX_SAFE_INTEGER, buckets: new Map() });
    const phase = phaseGroups.get(phaseKey);
    const key = equivalent.map((field) => String(entry[field] ?? "")).join("\u0000");
    if (!phase.buckets.has(key)) phase.buckets.set(key, []);
    phase.buckets.get(key).push(entry);
  });
  return [...phaseGroups.values()].sort((a, b) => a.order - b.order || collator.compare(a.name, b.name)).map((phase) => {
    const ranges = [];
    phase.buckets.forEach((bucket) => bucket.sort((a, b) => isoTime(a.datum) - isoTime(b.datum) || collator.compare(a.id || "", b.id || "")).forEach((entry) => {
      const previous = ranges[ranges.length - 1];
      if (previous && equivalent.every((key) => String(previous[key] ?? "") === String(entry[key] ?? "")) && isoTime(entry.datum) - isoTime(previous.to) === day) {
        previous.to = entry.datum; previous.ids.push(entry.id);
      } else ranges.push({ ...entry, from: entry.datum, to: entry.datum, ids: [entry.id] });
    }));
    ranges.sort((a, b) => isoTime(a.from) - isoTime(b.from) || collator.compare(a.employeeName, b.employeeName) || Number(a.stunden) - Number(b.stunden) || collator.compare(a.dispositionName, b.dispositionName) || collator.compare(a.inhouseExtern || "", b.inhouseExtern || "") || collator.compare(a.id || "", b.id || ""));
    return { id: phase.id, name: phase.name, entries: ranges };
  });
}

export function ProjectPlanningPanel({ projectId, planning, employees, phases, dispositions, planningYear }) {
  const period = planningYearPeriod(planningYear);
  const entries = (planning.data?.items || []).filter((entry) => entry.mandatId === projectId && inPlanningPeriod(entry, period));
  const groups = groupProjectPlanning(entries, employees.data?.items || [], phases.data?.items || [], dispositions.data?.items || []);
  const total = entries.reduce((sum, entry) => sum + (Number(entry.stunden) || 0), 0);
  const primaryLoading = planning.status === "loading" && !planning.data;
  const referenceErrors = [employees, phases, dispositions].filter((state) => state.status === "error");
  const retryAll = () => [planning, employees, phases, dispositions].forEach((state) => state.status === "error" && state.reload());
  return (
    <section className="project-planning-panel" aria-labelledby="project-planning-title">
      <header>
        <div><span className="live-dot" aria-hidden="true" /><h3 id="project-planning-title">Live aus der Planung</h3><small>{planningYearLabel(planningYear)}</small></div>
        <strong>{total.toLocaleString("de-CH")} h geplant</strong>
      </header>
      {primaryLoading ? <Notice>Daten werden geladen …</Notice> : planning.status === "error" && !planning.data ? (
        <Notice tone="error"><strong>{planning.error?.status === 403 ? "Kein Zugriff auf die Planung." : "Planung konnte nicht geladen werden."}</strong>{planning.error?.status !== 403 && <> {planning.error?.message} <Button variant="secondary" onClick={planning.reload}>Erneut versuchen</Button></>}</Notice>
      ) : <>
        {(planning.status === "error" || referenceErrors.length > 0) && <Notice tone="error"><strong>Ein Teil der Planungsdaten ist nicht aktuell.</strong> Vorhandene Einträge werden weiterhin angezeigt. <Button variant="secondary" onClick={retryAll}>Erneut versuchen</Button></Notice>}
        {groups.length === 0 ? <p className="empty-hint">Für dieses Projekt ist noch keine Planung erfasst.</p> : groups.map((phase) => (
          <section className="project-planning-phase" key={phase.id}>
            <h4>{phase.name}</h4>
            <div className="project-planning-rows">{phase.entries.map((entry) => (
              <div className="project-planning-row" key={entry.ids.join("-")}>
                <time>{dateLabel(entry.from)}{entry.to !== entry.from && ` – ${dateLabel(entry.to)}`}</time>
                <strong>{entry.employeeName}</strong><span>{Number(entry.stunden).toLocaleString("de-CH")} h</span>
                <span>{entry.dispositionName}</span><span>{entry.inhouseExtern || "–"}</span>
              </div>
            ))}</div>
          </section>
        ))}
      </>}
    </section>
  );
}
